import { AddressMap, useAddressMap } from 'components/common/AdressMap'
import { BEIGE_500, GRAY_900 } from 'constants/styling/theme'
import { Box, Stack, Typography } from '@mui/material'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { useEffect, useState } from 'react'

import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { Country } from 'constants/country'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { GoogleAPIPlace } from 'models/purchaseFlow'
import { Label } from 'components/common/Label'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUIInputField } from 'components/common/MUIInputField'
import i18n from 'translations/i18n'
import { lookupPlaceFromAddress } from 'utils/helpers/lookUpPlaceFromAddress'
import { usePurchaseFlowRealEstateProperty } from 'components/pages/PurchaseFlow/_main/contexts'
import { useTranslation } from 'react-i18next'

export interface Props {
  /** Function to update state from place */
  setSelectedPlace: (place: GoogleAPIPlace | null) => void
}

/**
 * @component ManualAddressCard component in Address step
 * @example <ManualAddressCard />
 */
export const ManualAddressCard: React.FC<Props> = ({ setSelectedPlace }) => {
  const { t } = useTranslation(['order', 'country'])
  const allowSecondMapPin = useFeatureFlag(FeatureFlag.ALLOW_SECOND_MAP_PIN)

  const {
    updateStateFromPlace,
    address,
    markerPosition,
  } = useAddressMap()

  const {
    isAddressManually,
    setIsAddressManually,
    addressManual,
    setAddressManual,
    streetNumberManual,
    setStreetNumberManual,
    cityManual,
    setCityManual,
    zipCodeManual,
    setZipCodeManual,
    countryManual,
    setCountryManual
  } = usePurchaseFlowRealEstateProperty()

  const [showError, setShowError] = useState<boolean>(false)

  const handleOnInputBlur = () => {
    if (!showError) setShowError(true)

    searchManualAddress()
  }

  const searchManualAddress = async () => {
    if (addressManual && streetNumberManual && cityManual && zipCodeManual && countryManual) {

      try {
        const manualAddressValue = `${addressManual} ${streetNumberManual}, ${cityManual}, ${zipCodeManual}, ${countryManual}`
        const lookupPlace = await lookupPlaceFromAddress(manualAddressValue)
        updateStateFromPlace(lookupPlace)
        setSelectedPlace(lookupPlace)
      } catch (error) {
        console.error('Error searching manual address', error)
      }

    }
  }

  useEffect(() => {

    searchManualAddress()

    // Call the searchManualAddress function when the countryManual changes, using the useEffect hook to prevent async update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryManual])

  return (
    <Stack gap="1.6rem">

      <BorderBoxWrapper
        padding={2}
        borderColor={BEIGE_500}
      >
        <Stack gap=".4rem">

          <Stack gap=".8rem" flexDirection="row" justifyContent="space-between">

            <Box width="100%">
              <MUIInputField
                required
                label={t('step_address.address')}
                value={addressManual}
                isError={showError && !addressManual && isAddressManually}
                onChange={(e) => setAddressManual(e.target.value)}
                onBlur={handleOnInputBlur}
              />
            </Box>

            <Box width="100%">
              <MUIInputField
                required
                label={t('step_address.street_number')}
                value={streetNumberManual}
                isError={showError && !streetNumberManual && isAddressManually}
                onChange={(e) => setStreetNumberManual(e.target.value)}
                onBlur={handleOnInputBlur}
              />
            </Box>

          </Stack>

          <Stack gap=".8rem" flexDirection="row" justifyContent="space-between">

            <Box width="100%">
              <MUIInputField
                required
                label={t('step_address.city')}
                value={cityManual}
                isError={showError && !cityManual && isAddressManually}
                onChange={(e) => setCityManual(e.target.value)}
                onBlur={handleOnInputBlur}
              />
            </Box>

            <Box width="100%">
              <MUIInputField
                required
                label={t('step_address.zip_code')}
                value={zipCodeManual}
                isError={showError && !zipCodeManual && isAddressManually}
                onChange={(e) => setZipCodeManual(e.target.value)}
                onBlur={handleOnInputBlur}
              />
            </Box>

          </Stack>

          <MUIDropdown
            button={(isOpen, action) => (
              <MUIDropdownInput
                readOnly
                required
                isError={showError && !countryManual && isAddressManually}
                label={t('step_address.country')}
                type={DropdownInputTypes.SELECTOR}
                value={countryManual ? t(`country:${countryManual}`) : countryManual}
                onClick={action}
              />
            )}
          >
            <Box height="30rem">
              {Object.values(Country)?.map(countryKey => (
                <MUIDropdownItem key={countryKey} onClick={() => setCountryManual(countryKey)}>
                  <Typography variant="text-md" color={GRAY_900} fontWeight="medium">
                    {t(`country:${countryKey}`, countryKey)}
                  </Typography>
                </MUIDropdownItem>
              ))}
            </Box>
          </MUIDropdown>

          {/* Second map for debugging purpose */}
          {allowSecondMapPin &&
            <Stack gap="1rem" marginTop="2rem">

              <Typography variant="text-sm" fontWeight={500} color={GRAY_900}>Second map for debugging the snapped address</Typography>

              <AddressMap
                showSecondMapPin
                height="40rem"
                draggable={false}
                language={i18n.language}
              />

              <Typography variant="text-sm" fontWeight={500} color={GRAY_900}>Snapped address: {address}</Typography>
              <Typography variant="text-sm" fontWeight={500} color={GRAY_900}>GEO: {markerPosition?.lat}, {markerPosition?.lng}</Typography>

            </Stack>
          }

        </Stack>
      </BorderBoxWrapper>

      <Label text={t('step_address.manual_address_note')} />

      <MUIButton
        type="secondaryBorder"
        size="xs"
        onClick={() => setIsAddressManually(false)}
      >
        {t('step_address.manual_address_back_button')}
      </MUIButton>

    </Stack>
  )
}
